import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { ArrowLeft, BellRing, Eye, Info, Mail, RefreshCw, RotateCcw, Save, Send, Undo2, X } from 'lucide-react';
import api from '../../../utils/authUtils';
import PlaceholderEditor, { KIND_STYLE } from './PlaceholderEditor';
import ReminderSendModal, { formatDay } from './ReminderSendModal';
import MarkDeclinedModal from './MarkDeclinedModal';
import {
  Container, BackLink, Header, Title, Subtitle, Card, CardTitle, Actions, Button, Message, Empty, Select, WORKSPACE,
} from './ui';
import { eventNameFor } from '../../../utils/eventName';

// Admin side of invite reminders (see SpeakerInviteReminderService): who is
// still waiting to answer an invitation, with manual sending, and the one
// global reminder template with its live preview.

const Tabs = ({ tab, setTab }) => (
  <div role="tablist" style={{ display: 'flex', gap: '.25rem', borderBottom: '2px solid #e2e8f0', marginBottom: '1rem' }}>
    {[
      ['pending', <BellRing key="i" size={15} />, <FormattedMessage key="l" id="inviteReminders.tab.pending" defaultMessage="Waiting for an answer" />],
      ['template', <Mail key="i" size={15} />, <FormattedMessage key="l" id="inviteReminders.tab.template" defaultMessage="Reminder email template" />],
    ].map(([key, icon, label]) => (
      <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '.4rem', border: 0, background: 'transparent', cursor: 'pointer',
          padding: '.6rem .9rem', font: 'inherit', fontWeight: 700, marginBottom: -2,
          color: tab === key ? '#111D4E' : '#64748b', borderBottom: `2px solid ${tab === key ? '#47ABDB' : 'transparent'}` }}>
        {icon}{label}
      </button>
    ))}
  </div>
);

// ---------------------------------------------------------------- pending

export const NextReminder = ({ invite, maxAutomatic }) => {
  const intl = useIntl();
  if (!invite.nextAutomaticReminder) {
    return <span style={{ color: '#64748b' }}><FormattedMessage id="inviteReminders.next.none" defaultMessage="No more automatic reminders ({max} sent)" values={{ max: maxAutomatic }} /></span>;
  }
  const next = new Date(`${invite.nextAutomaticReminder}T19:00:00`);
  if (next <= new Date()) {
    return <span style={{ color: '#92400e', fontWeight: 600 }}><FormattedMessage id="inviteReminders.next.due" defaultMessage="Due – goes out with the next daily run (19:00)" /></span>;
  }
  return <span>{formatDay(intl, next)}</span>;
};

const th = { textAlign: 'left', padding: '.55rem .6rem', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { padding: '.6rem', borderBottom: '1px solid #eef2f7', fontSize: '.85rem', verticalAlign: 'top' };

const PendingTab = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(null);
  const [declining, setDeclining] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: response } = await api.get('/api/cms/invite-reminders/pending');
      setData(response);
      setSelected(current => new Set([...current].filter(id => response.invites.some(i => i.invitationId === id))));
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else setError(intl.formatMessage({ id: 'inviteReminders.pending.loadFailed', defaultMessage: 'Could not load the pending invitations.' }));
    }
  }, [intl, navigate]);
  useEffect(() => { load(); }, [load]);

  if (error) return <Message error>{error}</Message>;
  if (!data) return <Empty><FormattedMessage id="inviteReminders.loading" defaultMessage="Loading…" /></Empty>;
  const invites = data.invites || [];
  const max = data.maxAutomaticReminders;
  const allSelected = invites.length > 0 && selected.size === invites.length;
  const toggle = id => setSelected(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  return (
    <>
      <Message style={{ background: '#eff6ff', color: '#1e3a8a', display: 'flex', gap: '.5rem', alignItems: 'flex-start' }}>
        <Info size={16} style={{ flex: 'none', marginTop: 2 }} />
        <span>
          <FormattedMessage id="inviteReminders.pending.rules"
            defaultMessage="Every day at 19:00, speakers who haven't answered get a reminder once {days} days have passed since the last email (the invitation or the previous reminder) – at most {max} times. Reminders you send yourself count toward those {max} and restart the {days}-day wait. Events that are over, and invitations that were accepted or declined, are left out. Speakers answer by clicking the button in the email and logging in; if someone declines another way, mark them as declined here."
            values={{ days: data.daysBetweenReminders, max }} />
        </span>
      </Message>
      <Card style={{ padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', padding: '1rem 1.25rem' }}>
          <CardTitle style={{ margin: 0 }}>
            <FormattedMessage id="inviteReminders.pending.title" defaultMessage="{count, plural, =0 {No invitations waiting for an answer} one {# invitation waiting for an answer} other {# invitations waiting for an answer}}" values={{ count: invites.length }} />
          </CardTitle>
          <Actions style={{ margin: 0 }}>
            <Button secondary onClick={load}><RefreshCw size={15} /><FormattedMessage id="speakerMgmt.refresh" defaultMessage="Refresh" /></Button>
            <Button disabled={selected.size === 0} onClick={() => setSending(invites.filter(i => selected.has(i.invitationId)))}>
              <Send size={15} /><FormattedMessage id="inviteReminders.pending.sendSelected" defaultMessage="{count, plural, =0 {Send reminders} one {Send reminder to # speaker} other {Send reminders to # speakers}}" values={{ count: selected.size }} />
            </Button>
          </Actions>
        </div>
        {invites.length === 0
          ? <Empty><FormattedMessage id="inviteReminders.pending.empty" defaultMessage="Everyone invited has answered – nothing to remind." /></Empty>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '52rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: '2rem' }}>
                      <input type="checkbox" checked={allSelected} aria-label={intl.formatMessage({ id: 'inviteReminders.pending.selectAll', defaultMessage: 'Select all' })}
                        onChange={() => setSelected(allSelected ? new Set() : new Set(invites.map(i => i.invitationId)))} />
                    </th>
                    <th style={th}><FormattedMessage id="inviteReminders.col.speaker" defaultMessage="Speaker" /></th>
                    <th style={th}><FormattedMessage id="inviteReminders.col.event" defaultMessage="Event" /></th>
                    <th style={th}><FormattedMessage id="inviteReminders.col.invited" defaultMessage="Invited" /></th>
                    <th style={th}><FormattedMessage id="inviteReminders.col.lastEmail" defaultMessage="Last email" /></th>
                    <th style={th}><FormattedMessage id="inviteReminders.col.reminders" defaultMessage="Reminders" /></th>
                    <th style={th}><FormattedMessage id="inviteReminders.col.next" defaultMessage="Next automatic reminder" /></th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {invites.map(invite => (
                    <tr key={invite.invitationId} style={{ background: selected.has(invite.invitationId) ? '#eff9fe' : undefined }}>
                      <td style={td}><input type="checkbox" checked={selected.has(invite.invitationId)} onChange={() => toggle(invite.invitationId)}
                        aria-label={invite.name} /></td>
                      <td style={td}>
                        {invite.speakerProfileId
                          ? <Link to={`${WORKSPACE}/${invite.speakerProfileId}`} style={{ fontWeight: 700, color: '#111D4E' }}>{invite.name}</Link>
                          : <strong>{invite.name}</strong>}
                        <div style={{ color: '#64748b', fontSize: '.78rem' }}>{invite.email}</div>
                      </td>
                      <td style={td}>
                        {eventNameFor(intl.locale, invite.eventName, invite.eventNameEN)}
                        {invite.eventDateTime && <div style={{ color: '#64748b', fontSize: '.78rem' }}>{formatDay(intl, invite.eventDateTime)}</div>}
                      </td>
                      <td style={td}>{formatDay(intl, invite.invitedAt)}</td>
                      <td style={td}>{formatDay(intl, invite.lastSentAt)}</td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: invite.reminderCount >= max ? '#991b1b' : '#0f172a' }}>
                        {invite.reminderCount} / {max}
                      </td>
                      <td style={td}><NextReminder invite={invite} maxAutomatic={max} /></td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '.4rem' }}>
                          <Button secondary onClick={() => setSending([invite])} style={{ whiteSpace: 'nowrap' }}>
                            <Eye size={14} /><FormattedMessage id="inviteReminders.pending.previewSend" defaultMessage="Preview & send" />
                          </Button>
                          {invite.speakerProfileId && (
                            <Button secondary onClick={() => setDeclining(invite)} style={{ whiteSpace: 'nowrap' }}
                              title={intl.formatMessage({ id: 'inviteReminders.decline.submit', defaultMessage: 'Mark as declined' })}>
                              <X size={14} /><FormattedMessage id="inviteReminders.pending.declined" defaultMessage="Declined" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
      {sending && <ReminderSendModal invites={sending} maxAutomatic={max} onClose={() => setSending(null)}
        onSent={() => { setSelected(new Set()); load(); }} />}
      {declining && <MarkDeclinedModal invite={declining} onClose={() => setDeclining(null)} onDone={load} />}
    </>
  );
};

// ---------------------------------------------------------------- template

// Placeholder copy is translated here; the backend's English text is the fallback.
const phText = (intl, p, field) => intl.formatMessage({ id: `inviteReminders.ph.${p.key}.${field}`, defaultMessage: p[field] });

const Legend = () => (
  <div style={{ display: 'flex', gap: '.9rem', flexWrap: 'wrap', fontSize: '.74rem', color: '#475569' }}>
    {[
      ['TEXT', <FormattedMessage key="t" id="inviteReminders.legend.text" defaultMessage="value from the database" />],
      ['BLOCK', <FormattedMessage key="b" id="inviteReminders.legend.block" defaultMessage="ready-made block (buttons, images, details)" />],
      ['UNKNOWN', <FormattedMessage key="u" id="inviteReminders.legend.unknown" defaultMessage="unknown placeholder" />],
    ].map(([kind, label]) => (
      <span key={kind} style={{ display: 'inline-flex', alignItems: 'center', gap: '.35rem' }}>
        <span style={{ width: 14, height: 10, borderRadius: 2, display: 'inline-block', ...KIND_STYLE[kind] }} />{label}
      </span>
    ))}
  </div>
);

const TemplateTab = () => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const navigate = useNavigate();
  const [saved, setSaved] = useState(null);
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [pending, setPending] = useState([]);
  const [sampleFor, setSampleFor] = useState('');
  const [highlight, setHighlight] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const lastFocused = useRef('html');
  const requestSeq = useRef(0);

  const apply = data => { setSaved(data); setSubject(data.subject); setHtml(data.html); };
  useEffect(() => {
    api.get('/api/cms/invite-reminders/template').then(({ data }) => apply(data)).catch(err => {
      if (err.response?.status === 401) navigate('/admin/login');
      else setMessage({ error: true, text: t('inviteReminders.template.loadFailed', 'Could not load the template.') });
    });
    api.get('/api/cms/invite-reminders/pending').then(({ data }) => setPending(data.invites || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const placeholders = useMemo(() => saved?.placeholders || [], [saved]);
  const kindOf = useCallback(key => placeholders.find(p => p.key === key)?.kind, [placeholders]);
  const groups = useMemo(() => placeholders.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {}), [placeholders]);

  // Live preview: the unsaved text, rendered by the same code as the real
  // send. previewOf records which text it was made from, so Save can insist
  // the admin is looking at a preview of exactly what they're saving.
  const contentKey = JSON.stringify([subject, html]);
  useEffect(() => {
    if (!saved) return undefined;
    const seq = ++requestSeq.current;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post('/api/cms/invite-reminders/preview', {
          subject, html, invitationId: sampleFor ? Number(sampleFor) : null, highlight,
        });
        if (seq === requestSeq.current) { setPreview({ ...data, of: contentKey }); setPreviewError(''); }
      } catch (err) {
        if (seq === requestSeq.current) {
          setPreviewError(typeof err.response?.data === 'string' && err.response.data
            ? err.response.data : t('inviteReminders.preview.error', 'Could not prepare the preview.'));
        }
      } finally {
        if (seq === requestSeq.current) setPreviewing(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [contentKey, sampleFor, highlight, saved]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!saved) return message ? <Message error>{message.text}</Message> : <Empty><FormattedMessage id="inviteReminders.loading" defaultMessage="Loading…" /></Empty>;

  const dirty = subject !== saved.subject || html !== saved.html;
  const problems = preview?.of === contentKey ? preview.problems || [] : null;
  const previewCurrent = preview?.of === contentKey && !previewing;
  const canSave = dirty && previewCurrent && problems && problems.length === 0 && !saving;

  const insert = key => {
    const token = `{{${key}}}`;
    const target = lastFocused.current === 'subject' ? subjectRef.current : htmlRef.current;
    const value = lastFocused.current === 'subject' ? subject : html;
    const set = lastFocused.current === 'subject' ? setSubject : setHtml;
    const start = target?.selectionStart ?? value.length;
    const end = target?.selectionEnd ?? value.length;
    set(value.slice(0, start) + token + value.slice(end));
    requestAnimationFrame(() => {
      if (!target) return;
      target.focus();
      target.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await api.put('/api/cms/invite-reminders/template', { subject, html });
      apply(data);
      setMessage({ text: t('inviteReminders.template.saved', 'Template saved. Every reminder from now on – automatic or manual – uses it.') });
    } catch (err) {
      setMessage({ error: true, text: typeof err.response?.data === 'string' && err.response.data ? err.response.data : t('inviteReminders.template.saveFailed', 'Could not save the template.') });
    } finally {
      setSaving(false);
    }
  };
  const reset = async () => {
    setConfirmReset(false);
    setMessage(null);
    try {
      const { data } = await api.delete('/api/cms/invite-reminders/template');
      apply(data);
      setMessage({ text: t('inviteReminders.template.resetDone', 'The built-in default template is in use again.') });
    } catch {
      setMessage({ error: true, text: t('inviteReminders.template.saveFailed', 'Could not save the template.') });
    }
  };
  const loadDefaultIntoEditor = () => { setSubject(saved.defaultSubject); setHtml(saved.defaultHtml); };

  const status = saved.isDefault
    ? <FormattedMessage id="inviteReminders.template.isDefault" defaultMessage="Using the built-in default template." />
    : <FormattedMessage id="inviteReminders.template.savedBy" defaultMessage="Last saved {date}{by, select, none {} other { by {by}}}."
        values={{ date: intl.formatDate(new Date(saved.updatedAt), { dateStyle: 'medium', timeStyle: 'short' }), by: saved.updatedBy || 'none' }} />;

  return (
    <>
      {message && <Message error={message.error}>{message.text}</Message>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 30rem), 1fr))', gap: '1rem', alignItems: 'start' }}>
        {/* ----- editor ----- */}
        <Card style={{ marginBottom: 0 }}>
          <CardTitle><FormattedMessage id="inviteReminders.template.editTitle" defaultMessage="Edit the reminder" /></CardTitle>
          <p style={{ margin: '-.5rem 0 1rem', fontSize: '.8rem', color: '#64748b' }}>
            <FormattedMessage id="inviteReminders.template.intro"
              defaultMessage="One template for all events and speakers. Write the text as HTML; anything in double curly braces is filled in for each speaker when the email goes out. Click a placeholder below to insert it where your cursor is." />
          </p>
          <label style={{ display: 'block', fontSize: '.82rem', fontWeight: 600, color: '#334155', marginBottom: '.35rem' }}>
            <FormattedMessage id="inviteReminders.template.subject" defaultMessage="Subject" />
          </label>
          <PlaceholderEditor ref={subjectRef} singleLine value={subject} onChange={setSubject} kindOf={kindOf}
            onFocus={() => { lastFocused.current = 'subject'; }} ariaLabel={t('inviteReminders.template.subject', 'Subject')} />
          <label style={{ display: 'block', fontSize: '.82rem', fontWeight: 600, color: '#334155', margin: '1rem 0 .35rem' }}>
            <FormattedMessage id="inviteReminders.template.body" defaultMessage="Email (HTML)" />
          </label>
          <PlaceholderEditor ref={htmlRef} value={html} onChange={setHtml} kindOf={kindOf} rows={22}
            onFocus={() => { lastFocused.current = 'html'; }} ariaLabel={t('inviteReminders.template.body', 'Email (HTML)')} />
          <div style={{ marginTop: '.5rem' }}><Legend /></div>

          <div style={{ marginTop: '1.25rem' }}>
            <div style={{ fontSize: '.82rem', fontWeight: 700, color: '#334155', marginBottom: '.5rem' }}>
              <FormattedMessage id="inviteReminders.template.placeholders" defaultMessage="Placeholders" />
            </div>
            {Object.entries(groups).map(([group, items]) => (
              <div key={group} style={{ marginBottom: '.8rem' }}>
                <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b', fontWeight: 700, marginBottom: '.35rem' }}>
                  {t(`inviteReminders.group.${group}`, group)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                  {items.map(p => (
                    <button key={p.key} type="button" onClick={() => insert(p.key)}
                      title={t('inviteReminders.template.insert', 'Insert {token}', { token: `{{${p.key}}}` })}
                      style={{ display: 'flex', gap: '.6rem', alignItems: 'baseline', textAlign: 'left', border: '1px solid #e2e8f0', background: '#fff',
                        padding: '.4rem .55rem', cursor: 'pointer', font: 'inherit', borderRadius: 4 }}>
                      <code style={{ flex: 'none', fontSize: '.75rem', padding: '0 .3rem', borderRadius: 3, ...KIND_STYLE[p.kind] }}>{`{{${p.key}}}`}</code>
                      <span style={{ fontSize: '.78rem', color: '#334155', lineHeight: 1.4 }}>
                        <strong>{phText(intl, p, 'label')}</strong> — {phText(intl, p, 'description')}
                        <span style={{ display: 'block', color: '#94a3b8', fontSize: '.72rem' }}>
                          <FormattedMessage id="inviteReminders.template.source" defaultMessage="Comes from: {source}" values={{ source: phText(intl, p, 'source') }} />
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ----- preview ----- */}
        <Card style={{ marginBottom: 0, position: 'sticky', top: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
            <CardTitle style={{ margin: 0 }}><Eye size={17} style={{ verticalAlign: '-3px', marginRight: '.35rem' }} />
              <FormattedMessage id="inviteReminders.preview.title" defaultMessage="Preview of the reminder" />
            </CardTitle>
            {previewing && <span style={{ fontSize: '.75rem', color: '#64748b' }}><FormattedMessage id="inviteReminders.preview.updating" defaultMessage="Updating…" /></span>}
          </div>
          <div style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '.6rem', fontSize: '.8rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '.4rem', flex: '1 1 16rem', fontWeight: 600, color: '#334155' }}>
              <FormattedMessage id="inviteReminders.preview.as" defaultMessage="Fill in for" />
              <Select value={sampleFor} onChange={e => setSampleFor(e.target.value)} style={{ flex: 1, minWidth: 0, padding: '.4rem .5rem' }}>
                <option value="">{t('inviteReminders.preview.sample', 'Sample speaker (Dr. Jane Doe, main event)')}</option>
                {pending.map(i => <option key={i.invitationId} value={i.invitationId}>{i.name} — {eventNameFor(intl.locale, i.eventName, i.eventNameEN)}</option>)}
              </Select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '.35rem', color: '#334155' }}>
              <input type="checkbox" checked={highlight} onChange={e => setHighlight(e.target.checked)} />
              <FormattedMessage id="inviteReminders.preview.highlight" defaultMessage="Mark filled-in parts" />
            </label>
          </div>
          {highlight && (
            <div style={{ fontSize: '.74rem', color: '#64748b', marginBottom: '.5rem' }}>
              <FormattedMessage id="inviteReminders.preview.highlightHint"
                defaultMessage="Marked parts are filled in per speaker – hover one to see which placeholder it is. The markings never appear in the real email." />
            </div>
          )}
          <div style={{ fontSize: '.82rem', color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderBottom: 0, padding: '.45rem .6rem' }}>
            <strong><FormattedMessage id="speakerMgmt.preview.subject" defaultMessage="Subject" />:</strong> {preview?.subject || '…'}
          </div>
          {previewError
            ? <Message error style={{ marginTop: '.5rem' }}>{previewError}</Message>
            : <iframe title={t('inviteReminders.preview.title', 'Preview of the reminder')} sandbox="" srcDoc={preview ? `<base target="_blank">${preview.html}` : ''}
                style={{ width: '100%', height: '70vh', border: '1px solid #e2e8f0', background: '#fff', opacity: previewing ? .6 : 1, transition: 'opacity .15s' }} />}
        </Card>
      </div>

      {/* ----- save bar ----- */}
      <Card style={{ position: 'sticky', bottom: 0, marginTop: '1rem', marginBottom: 0, zIndex: 5, boxShadow: '0 -6px 20px rgba(15,23,42,.08)' }}>
        {problems && problems.length > 0 && (
          <Message error style={{ marginBottom: '.75rem' }}>
            <strong><FormattedMessage id="inviteReminders.template.problems" defaultMessage="This can't be saved yet:" /></strong>
            <ul style={{ margin: '.3rem 0 0', paddingLeft: '1.1rem' }}>{problems.map(p => <li key={p}>{p}</li>)}</ul>
          </Message>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '.82rem', color: dirty ? '#92400e' : '#64748b', fontWeight: dirty ? 700 : 400 }}>
            {dirty ? <FormattedMessage id="inviteReminders.template.unsaved" defaultMessage="Unsaved changes – check the preview, then save." /> : status}
          </span>
          <Actions style={{ margin: 0 }}>
            {dirty && <Button secondary onClick={() => apply(saved)}><Undo2 size={15} /><FormattedMessage id="inviteReminders.template.discard" defaultMessage="Discard changes" /></Button>}
            {!saved.isDefault && <Button secondary onClick={() => setConfirmReset(true)}><RotateCcw size={15} /><FormattedMessage id="inviteReminders.template.reset" defaultMessage="Reset to default" /></Button>}
            <Button disabled={!canSave} onClick={save}
              title={dirty && !previewCurrent ? t('inviteReminders.template.waitPreview', 'Wait for the preview to show your latest changes') : undefined}>
              <Save size={15} />{saving ? <FormattedMessage id="inviteReminders.template.saving" defaultMessage="Saving…" /> : <FormattedMessage id="inviteReminders.template.save" defaultMessage="Save template" />}
            </Button>
          </Actions>
        </div>
        {confirmReset && (
          <Message warn style={{ marginTop: '.75rem', marginBottom: 0 }}>
            <FormattedMessage id="inviteReminders.template.resetConfirm" defaultMessage="Replace the saved template with the built-in default? Your saved version is deleted. (To compare first, load the default into the editor – nothing is saved until you click Save.)" />
            <Actions style={{ marginTop: '.5rem' }}>
              <Button secondary onClick={() => setConfirmReset(false)}><FormattedMessage id="speakerMgmt.confirm.cancel" defaultMessage="Cancel" /></Button>
              <Button secondary onClick={() => { setConfirmReset(false); loadDefaultIntoEditor(); }}><FormattedMessage id="inviteReminders.template.loadDefault" defaultMessage="Load default into editor" /></Button>
              <Button danger onClick={reset}><RotateCcw size={15} /><FormattedMessage id="inviteReminders.template.resetNow" defaultMessage="Reset now" /></Button>
            </Actions>
          </Message>
        )}
      </Card>
    </>
  );
};

// ---------------------------------------------------------------- page

const InviteReminders = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'template' ? 'template' : 'pending';
  const setTab = value => setSearchParams(value === 'pending' ? {} : { tab: value }, { replace: true });
  return (
    <Container>
      <BackLink to={WORKSPACE}><ArrowLeft size={16} /><FormattedMessage id="speakerMgmt.backList" defaultMessage="Back to speakers" /></BackLink>
      <Header>
        <Title><FormattedMessage id="inviteReminders.title" defaultMessage="Invite reminders" /></Title>
      </Header>
      <Subtitle><FormattedMessage id="inviteReminders.subtitle" defaultMessage="Remind invited speakers who haven't answered yet – automatically, or yourself – and edit the reminder email." /></Subtitle>
      <Tabs tab={tab} setTab={setTab} />
      {tab === 'pending' ? <PendingTab /> : <TemplateTab />}
    </Container>
  );
};

export default InviteReminders;
